(() => {
  if (window.WeRoll0526Loader) {
    window.WeRoll0526Loader.scan(document);
    return;
  }

  const modulePromises = new Map();
  const rootPromises = new WeakMap();
  const observedRoots = new WeakSet();

  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }
    callback();
  };

  const onLoad = (callback) => {
    if (document.readyState === 'complete') {
      callback();
      return;
    }
    window.addEventListener('load', callback, { once: true });
  };

  const idle = (callback, timeout = 1500) => {
    if ('requestIdleCallback' in window) {
      return window.requestIdleCallback(callback, { timeout });
    }
    return window.setTimeout(() => callback({ didTimeout: true, timeRemaining: () => 0 }), 1);
  };

  const loadModule = (source) => {
    if (!source) return Promise.resolve();
    if (modulePromises.has(source)) return modulePromises.get(source);

    const promise = new Promise((resolve, reject) => {
      const existing = Array.from(document.scripts).find((script) => script.src === source);
      if (existing) {
        if (existing.dataset.werollLoaded === 'true') {
          resolve();
          return;
        }
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = source;
      script.async = true;
      script.dataset.werollModuleScript = '';
      script.addEventListener('load', () => {
        script.dataset.werollLoaded = 'true';
        resolve();
      }, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });

    modulePromises.set(source, promise);
    return promise;
  };

  const activate = (root) => {
    if (!root || root.dataset.werollModuleReady === 'true') return Promise.resolve();
    if (rootPromises.has(root)) return rootPromises.get(root);
    root.dataset.werollModuleActivated = 'true';
    const promise = loadModule(root.dataset.werollModule)
      .then(() => root.dispatchEvent(new CustomEvent('weroll:module:loaded')))
      .catch(() => {
        delete root.dataset.werollModuleActivated;
        rootPromises.delete(root);
      });
    rootPromises.set(root, promise);
    return promise;
  };

  const observeVisible = (root) => {
    if (!('IntersectionObserver' in window)) {
      idle(() => activate(root), 1200);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      activate(root);
    }, {
      rootMargin: root.dataset.werollModuleMargin || '500px 0px',
      threshold: 0.01
    });

    observer.observe(root);
  };

  const bindInteraction = (root) => {
    const selector = root.dataset.werollModuleSelector || 'button, [role="button"], summary';

    const prewarm = (event) => {
      if (event.target.closest(selector)) activate(root);
    };

    root.addEventListener('pointerdown', prewarm, { passive: true });
    root.addEventListener('focusin', prewarm, { passive: true });
    root.addEventListener('click', (event) => {
      const target = event.target.closest(selector);
      if (!target || root.dataset.werollModuleReady === 'true') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      activate(root).then(() => {
        if (target.isConnected) target.click();
      });
    }, true);
  };

  const setupRoot = (root) => {
    if (!root || observedRoots.has(root) || !root.dataset.werollModule) return;
    observedRoots.add(root);

    const trigger = root.dataset.werollModuleTrigger || 'visible';
    if (trigger === 'interaction') {
      bindInteraction(root);
      return;
    }
    if (trigger === 'idle') {
      onLoad(() => idle(() => activate(root), Number(root.dataset.werollModuleTimeout) || 1800));
      return;
    }
    observeVisible(root);

    if (root.dataset.werollModuleSelector) {
      const prewarm = (event) => {
        if (event.target.closest(root.dataset.werollModuleSelector)) activate(root);
      };
      root.addEventListener('pointerdown', prewarm, { passive: true });
      root.addEventListener('focusin', prewarm, { passive: true });
    }
  };

  const scan = (scope = document) => {
    if (scope.matches && scope.matches('[data-weroll-module]')) setupRoot(scope);
    scope.querySelectorAll?.('[data-weroll-module]').forEach(setupRoot);
  };

  window.WeRoll0526Loader = { activate, idle, loadModule, scan };
  onReady(() => scan(document));
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
